import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, Eye, X } from 'lucide-react'

export interface ContextMenuTarget {
  identifier: string
  x: number
  y: number
}

/**
 * The right-click menu on a gallery tile.
 *
 * Small on purpose. A context menu on this screen is a shortcut for people who
 * expect one, not a second home for features — everything in it is reachable by
 * tapping or long-pressing the tile.
 *
 * Positioned at the cursor and then pulled back inside the viewport, because a
 * right-click near the right or bottom edge of a 1366x768 console otherwise
 * opens a menu half off-screen.
 */
export function PhotoContextMenu({
  target,
  selected,
  onPreview,
  onToggleSelect,
  onClose,
}: {
  target: ContextMenuTarget
  selected: boolean
  onPreview: () => void
  onToggleSelect: () => void
  onClose: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState({ left: target.x, top: target.y })

  useEffect(() => {
    const element = ref.current
    if (!element) return
    const box = element.getBoundingClientRect()
    setPosition({
      left: Math.min(target.x, window.innerWidth - box.width - 8),
      top: Math.min(target.y, window.innerHeight - box.height - 8),
    })
  }, [target])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // Rendered into <body>. The step wrapper carries a CSS animation with
  // `fill-mode: both`, so its transform stays applied after the animation ends
  // and makes it the containing block for anything `position: fixed` inside it
  // — which put this menu ~95px below the cursor and let it run off the bottom
  // of a 768px console.
  return createPortal(
    <>
      {/* Catches the next click anywhere, including a second right-click. */}
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
        onContextMenu={(event) => {
          event.preventDefault()
          onClose()
        }}
      />
      <div
        ref={ref}
        role="menu"
        style={{ left: position.left, top: position.top }}
        className="fixed z-50 min-w-[190px] overflow-hidden rounded-[12px] bg-raised py-1 shadow-2xl ring-1 ring-line"
      >
        <Item icon={<Eye size={16} />} label="Pratinjau" onClick={onPreview} />
        <Item
          icon={selected ? <X size={16} /> : <Check size={16} />}
          label={selected ? 'Batal pilih' : 'Pilih foto'}
          onClick={onToggleSelect}
        />
      </div>
    </>,
    document.body,
  )
}

function Item({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="flex min-h-[44px] w-full items-center gap-3 px-4 text-left text-[14px] text-ink active:bg-line"
    >
      <span className="shrink-0 text-ink-muted">{icon}</span>
      {label}
    </button>
  )
}
